import React, { useEffect, useRef, useState } from "react";
import { Modal, message as toast } from "antd";
import axios from "../axios/AxiosInstance";
import { apiErrorMessage } from "../utils/apiError";
import CvAnonymizerModal from "./CvAnonymizerModal";
const contactsFor = (client) =>
  Array.from(
    new Map(
      [
        ...(client?.contactEMail
          ? [
              {
                email: client.contactEMail,
                firstname: client.contactFirstname,
                lastname: client.contactLastname,
              },
            ]
          : []),
        ...(client?.contacts || []),
      ]
        .filter((c) => c.email && c.active !== false)
        .map((c) => [c.email.trim().toLowerCase(), c]),
    ).values(),
  );
const fold = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
export default function SendAnonymousCvModal({
  candidate,
  onClose,
  onSent,
  initialClientId = "",
}) {
  const [clients, setClients] = useState([]),
    [orders, setOrders] = useState([]),
    [selected, setSelected] = useState(
      initialClientId ? [initialClientId] : [],
    ),
    [targets, setTargets] = useState({}),
    [search, setSearch] = useState("");
  const [subject, setSubject] = useState("Proposition de profil — JM Intérim");
  const [body, setBody] = useState(
    `Bonjour,\n\nJe vous propose ce profil pour vos recrutements :\n${(
      candidate.qualifications || []
    )
      .map((q) => (typeof q === "string" ? q : q.label || q.name))
      .filter(Boolean)
      .join(
        ", ",
      )}\n\nVous trouverez son CV anonymisé en pièce jointe. Je reste à votre disposition pour échanger sur votre besoin.\n\nBien cordialement,`,
  );
  const [editor, setEditor] = useState(false),
    [file, setFile] = useState(null),
    [url, setUrl] = useState(""),
    [confirmed, setConfirmed] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [ready, setReady] = useState(false),
    [sent, setSent] = useState({});
  const [savedCv, setSavedCv] = useState(null);
  const [savedLoading, setSavedLoading] = useState(true);
  const [savingCv, setSavingCv] = useState(false);
  const [saveNotice, setSaveNotice] = useState("");
  useEffect(() => {
    let active = true;
    axios
      .get(`cv-delivery/candidate/${candidate._id}/saved`)
      .then(({ data }) => {
        if (active) setSavedCv(data?.data ? data : null);
      })
      .catch(() => {
        if (active)
          setSaveNotice(
            "Impossible de récupérer le CV enregistré. Vous pouvez préparer un nouveau CV.",
          );
      })
      .finally(() => {
        if (active) setSavedLoading(false);
      });
    return () => {
      active = false;
    };
  }, [candidate._id]);
  const reuseCv = () => {
    try {
      const bytes = Uint8Array.from(atob(savedCv.data), (char) =>
        char.charCodeAt(0),
      );
      setFile(new File([bytes], savedCv.filename, { type: "application/pdf" }));
      setConfirmed(false);
      setSaveNotice("");
      requestIds.current = {};
    } catch {
      setSaveNotice(
        "Le CV enregistré est illisible. Préparez une nouvelle version.",
      );
    }
  };
  const saveCv = async () => {
    if (!file || !confirmed || savingCv) return;
    setSavingCv(true);
    setSaveNotice("");
    const form = new FormData();
    form.append("file", file);
    form.append("previewConfirmed", "true");
    try {
      await axios.post(`cv-delivery/candidate/${candidate._id}/saved`, form);
      setSaveNotice(
        "CV anonymisé enregistré : vous pourrez le réutiliser pour d’autres clients.",
      );
    } catch (e) {
      setSaveNotice(
        apiErrorMessage(e, "Impossible d’enregistrer le CV. Réessayez."),
      );
    } finally {
      setSavingCv(false);
    }
  };
  const requestIds = useRef({});
  useEffect(() => {
    let active = true;
    Promise.all([axios.get("client/freelance"), axios.get("order/freelance")])
      .then(([c, o]) => {
        if (active) {
          setClients(Array.isArray(c.data) ? c.data : []);
          setOrders(Array.isArray(o.data) ? o.data : []);
          setReady(true);
        }
      })
      .catch((e) => {
        if (active)
          setError(apiErrorMessage(e, "Impossible de charger les clients."));
      });
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    if (!file) {
      setUrl("");
      return;
    }
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);
  const updateTarget = (id, key, value) => {
    setTargets((prev) => ({ ...prev, [id]: { ...prev[id], [key]: value } }));
    delete requestIds.current[id];
  };
  const pending = selected.filter((id) => !sent[id]);
  const send = async () => {
    if (
      busy ||
      savingCv ||
      !confirmed ||
      !file ||
      !pending.length ||
      pending.some((id) => !targets[id]?.recipientEmail)
    )
      return;
    setBusy(true);
    setError("");
    const failures = [];
    let sentCount = 0;
    for (const clientId of pending) {
      const data = new FormData();
      const target = targets[clientId];
      const requestId = (requestIds.current[clientId] ||= crypto.randomUUID());
      Object.entries({
        clientId,
        recipientEmail: target.recipientEmail,
        orderId: target.orderId || "",
        subject,
        message: body,
        previewConfirmed: "true",
        requestId,
      }).forEach(([key, value]) => data.append(key, value));
      data.append("file", file);
      try {
        const { data: result } = await axios.post(
          `cv-delivery/candidate/${candidate._id}`,
          data,
        );
        if (result.status !== "sent")
          throw new Error(
            "Envoi non confirmé : consultez l’historique avant de réessayer.",
          );
        setSent((prev) => ({ ...prev, [clientId]: true }));
        sentCount++;
        window.dispatchEvent(new Event("jm-cv-delivery-updated"));
      } catch (e) {
        failures.push(
          `${clients.find((c) => c._id === clientId)?.companyName || "Client"} : ${apiErrorMessage(e, "Envoi non confirmé. Consultez l’historique avant de réessayer.")}`,
        );
      }
    }
    setBusy(false);
    onSent?.();
    if (failures.length) setError(failures.join("\n"));
    else {
      toast.success(
        `CV anonymisé envoyé à ${sentCount} client${sentCount > 1 ? "s" : ""}.`,
      );
      onClose();
    }
  };
  const input = "w-full rounded-lg border p-2 mt-1";
  const matching = clients.filter((c) =>
    fold(`${c.companyName} ${c.town || ""}`).includes(fold(search)),
  );
  const locked = Object.values(sent).some(Boolean);
  return (
    <>
      <Modal
        open
        title="Envoyer un CV anonymisé"
        width={980}
        zIndex={10000}
        onCancel={() => {
          if (!busy && !savingCv) onClose();
        }}
        footer={null}
        maskClosable={!busy && !savingCv}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
          className="space-y-4"
        >
          {error && (
            <p
              role="alert"
              className="rounded bg-red-50 p-3 text-red-700 whitespace-pre-line"
            >
              {error}
            </p>
          )}
          <fieldset disabled={busy || !ready} className="space-y-4">
            <div>
              <label htmlFor="cv-client-search">Rechercher un client</label>
              <input
                id="cv-client-search"
                className={input}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nom ou ville du client…"
              />
            </div>
            <fieldset className="rounded-lg border p-3">
              <legend>Clients destinataires · plusieurs choix possibles</legend>
              <div className="max-h-40 overflow-auto space-y-2">
                {matching.slice(0, 50).map((client) => (
                  <label key={client._id} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selected.includes(client._id)}
                      disabled={!!sent[client._id]}
                      onChange={(e) =>
                        setSelected((prev) =>
                          e.target.checked
                            ? [...prev, client._id]
                            : prev.filter((id) => id !== client._id),
                        )
                      }
                    />
                    {client.companyName}
                  </label>
                ))}
                {!matching.length && <p>Aucun client trouvé.</p>}
                {matching.length > 50 && (
                  <p className="text-sm text-slate-500">
                    Précisez la recherche pour afficher les autres clients.
                  </p>
                )}
              </div>
            </fieldset>
            {selected.map((id) => {
              const client = clients.find((c) => c._id === id);
              if (!client) return null;
              const contacts = contactsFor(client);
              return (
                <section key={id} className="rounded-lg border p-3 space-y-2">
                  <h3 className="font-semibold">
                    {client.companyName}
                    {sent[id] && (
                      <span className="ml-2 text-green-700">Envoyé</span>
                    )}
                  </h3>
                  <label className="block">
                    Contact destinataire — {client.companyName}
                    <select
                      required={!sent[id]}
                      disabled={!!sent[id]}
                      className={input}
                      value={targets[id]?.recipientEmail || ""}
                      onChange={(e) =>
                        updateTarget(id, "recipientEmail", e.target.value)
                      }
                    >
                      <option value="">Choisir un contact</option>
                      {contacts.map((c) => (
                        <option key={c.email} value={c.email}>
                          {[c.firstname, c.lastname].filter(Boolean).join(" ")}{" "}
                          · {c.email}
                        </option>
                      ))}
                    </select>
                  </label>
                  {!contacts.length && (
                    <p className="text-amber-700">
                      Ajoutez une adresse email dans la fiche de ce client.
                    </p>
                  )}
                  <label className="block">
                    Commande associée — {client.companyName} (facultatif)
                    <select
                      disabled={!!sent[id]}
                      className={input}
                      value={targets[id]?.orderId || ""}
                      onChange={(e) =>
                        updateTarget(id, "orderId", e.target.value)
                      }
                    >
                      <option value="">Aucune commande</option>
                      {orders
                        .filter((o) => String(o.client?._id || o.client) === id)
                        .map((o) => (
                          <option key={o._id} value={o._id}>
                            N° {o.orderNumber || o._id} ·{" "}
                            {o.qualification || "Commande"}
                          </option>
                        ))}
                    </select>
                  </label>
                </section>
              );
            })}
            <section
              className="rounded-lg border border-slate-200 p-3 space-y-2"
              aria-label="CV anonymisé enregistré"
            >
              <p className="font-semibold">Votre CV anonymisé</p>
              {savedLoading ? (
                <p className="text-sm text-slate-500">
                  Recherche d’une version enregistrée…
                </p>
              ) : savedCv ? (
                <>
                  <p className="text-sm text-slate-600">
                    Une version est disponible : réutilisez-la pour un autre
                    client.
                  </p>
                  {savedCv.outdated && (
                    <p role="alert" className="text-sm text-amber-700">
                      Le CV principal a changé depuis cette version. Vérifiez
                      son contenu ou préparez un nouveau CV anonymisé.
                    </p>
                  )}
                  {savedCv.sourceUnverified && (
                    <p className="text-sm text-amber-700">
                      Vérifiez que cette version correspond au CV actuel du
                      candidat.
                    </p>
                  )}
                  <button
                    type="button"
                    disabled={locked || savingCv}
                    onClick={reuseCv}
                    className="rounded-lg border border-blue-700 px-3 py-2 text-blue-800 disabled:opacity-50"
                  >
                    Réutiliser le CV enregistré
                  </button>
                </>
              ) : (
                <p className="text-sm text-slate-500">
                  Préparez une première version. Elle sera conservée lors de
                  l’envoi.
                </p>
              )}
            </section>
            <button
              type="button"
              disabled={locked || savingCv}
              className="rounded-lg bg-blue-700 px-4 py-2 text-white"
              onClick={() => {
                setConfirmed(false);
                setEditor(true);
              }}
            >
              Anonymiser et prévisualiser le CV
            </button>
            {url && (
              <div>
                <iframe
                  title="Prévisualisation du CV anonymisé"
                  src={url}
                  className="w-full h-[50vh] border"
                />
                <label className="flex gap-2 mt-3">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    disabled={locked}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    required
                  />
                  J’ai vérifié toutes les pages : identité, photo, coordonnées,
                  adresse précise et liens personnels sont masqués.
                </label>
              </div>
            )}
            {file && (
              <button
                type="button"
                disabled={!confirmed || savingCv || locked}
                onClick={saveCv}
                className="rounded-lg border border-blue-700 px-3 py-2 text-sm text-blue-800 disabled:opacity-50"
              >
                {savingCv ? "Enregistrement…" : "Enregistrer pour réutiliser"}
              </button>
            )}
            {saveNotice && (
              <p role="status" className="text-sm text-slate-600">
                {saveNotice}
              </p>
            )}
            <label className="block">
              Objet
              <input
                required
                disabled={locked}
                maxLength={200}
                className={input}
                value={subject}
                onChange={(e) => {
                  setSubject(e.target.value);
                  requestIds.current = {};
                }}
              />
            </label>
            <label className="block">
              Message
              <textarea
                required
                disabled={locked}
                rows={7}
                maxLength={10000}
                className={input}
                value={body}
                onChange={(e) => {
                  setBody(e.target.value);
                  requestIds.current = {};
                }}
              />
            </label>
            <p className="text-sm text-slate-500">
              Chaque client reçoit un email individuel. La signature JM Intérim
              et vos coordonnées seront ajoutées à l’email.
            </p>
          </fieldset>
          <div className="flex justify-end gap-3">
            <button type="button" disabled={busy || savingCv} onClick={onClose}>
              Fermer
            </button>
            <button
              type="submit"
              disabled={
                busy ||
                savingCv ||
                !ready ||
                !file ||
                !confirmed ||
                !pending.length ||
                pending.some((id) => !targets[id]?.recipientEmail)
              }
              className="rounded-lg bg-blue-700 px-4 py-2 text-white disabled:opacity-50"
            >
              {busy
                ? "Envoi en cours…"
                : locked
                  ? "Réessayer les envois restants"
                  : "Envoyer le CV anonymisé"}
            </button>
          </div>
        </form>
      </Modal>
      {editor && (
        <CvAnonymizerModal
          open
          candidate={candidate}
          onClose={() => setEditor(false)}
          onConfirm={(next) => {
            setFile(next);
            setConfirmed(false);
            requestIds.current = {};
          }}
        />
      )}
    </>
  );
}
